/**
 * useIndividualDerivation — §8 Individual entity-type derivation engine.
 *
 * Within the Personal entity group, `customerType` is auto-derived and
 * read-only. EntityTypePicker displays the derived type with a
 * "(derived from X)" explanation; not manually selectable.
 *
 * Derivation rules (in order):
 *   1. baseline = 'Individual'
 *   2. DOB valid AND age < 18 → 'Minor' (from='dob')
 *   3. citizenship !== 'US Citizen' → 'NonCitizen' (from='citizenship')
 *      - if already 'Minor' AND admin toggle precedence.minorVsNonCitizen
 *        === 'minor' (default), Minor wins and keeps from='dob'
 *      - else NonCitizen wins
 *
 * Existing-CIF reload behaviour:
 *   - If mode === 'existing' and storedType !== derived type:
 *     - autoPromoteOnReload === false: return stored (no promotion)
 *     - autoPromoteOnReload === true:  return derived, set promotedFrom
 *     - supervisorOverrideRequired === true AND officer is not a
 *       supervisor (a role in SystemSetting `override.supervisor.roles`,
 *       BACKLOG-1787): requiresSupervisorApproval = true (gate the CustMod)
 *     - EXEMPT (Bill 2026-07-27): a DOB-derived Individual → Minor
 *       promotion never requires supervisor approval — it is arithmetic,
 *       not judgment.
 *
 * Admin toggles are read from useConfig() -> ConfigBundle.systemSettings.
 * Defaults documented in `docs/port-design/people-form-port.md` §8.
 */

import { useMemo } from 'react';
import { useConfig } from '@/contexts/ConfigContext';
import { useAuth } from '@/contexts/AuthContext';
import type { RaoPerson } from '../../../types';
import { ageFromDob } from '../../../utils/age';
import {
  holdsSupervisorOverrideRole,
  supervisorOverrideRolesFrom,
} from '../shared/supervisorOverrideRoles';

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

export type DerivedCustomerType = 'Individual' | 'Minor' | 'NonCitizen';

export type DerivationSource = 'baseline' | 'dob' | 'citizenship';

export interface DerivationResult {
  /** The auto-derived customer type for this Person. */
  readonly customerType: DerivedCustomerType;
  /** Which field drove the derivation. */
  readonly derivedFrom: DerivationSource;
  /**
   * For existing-CIF reload: the customerType that was on file in Core
   * Director before the derivation engine ran. Present only when
   * `autoPromoteOnReload === true` and the stored value differs from the
   * derived value. UI uses this to render the one-line "Entity type updated
   * from 'X' to 'Y'" note.
   */
  readonly promotedFrom?: string;
  /**
   * True when this CIF needs a supervisor to approve the auto-promotion
   * before any CustMod can commit. UI should freeze the save button and
   * surface a supervisor-approval widget.
   */
  readonly requiresSupervisorApproval?: boolean;
}

export interface UseIndividualDerivationOptions {
  /** 'new' = fresh capture; 'existing' = CustInq reload of pre-existing CIF. */
  readonly mode: 'new' | 'existing';
  /** The customerType that Core Director had on file for this CIF (existing only). */
  readonly storedType?: string;
}

// -----------------------------------------------------------------------------
// Admin-toggle shape (read from config bundle; falls back to documented defaults)
// -----------------------------------------------------------------------------

interface IndividualDerivationToggles {
  /** Who wins when both Minor + NonCitizen conditions apply. Default 'minor'. */
  minorVsNonCitizen: 'minor' | 'nonCitizen';
  /** Re-derive customerType on existing-CIF edit. Default true. */
  autoPromoteOnReload: boolean;
  /** Require supervisor approval when auto-promotion changes stored type. Default true. */
  supervisorOverrideRequired: boolean;
}

function readToggles(config: unknown): IndividualDerivationToggles {
  // SystemSettings is a key/value catalog; the real SQL-backed shape is TBD
  // (§8 admin toggle registry). For now we defensively probe a few likely
  // shapes so this hook works whether the catalog lands as:
  //   - config.systemSettings: Record<string, unknown>
  //   - config.systemSettings: Array<{ key, value }>
  //   - not present at all (use defaults)
  const defaults: IndividualDerivationToggles = {
    minorVsNonCitizen: 'minor',
    autoPromoteOnReload: true,
    supervisorOverrideRequired: true,
  };

  if (!config || typeof config !== 'object') return defaults;
  const cfg = config as Record<string, unknown>;
  const ss = cfg.systemSettings;

  const pickBool = (v: unknown, fallback: boolean): boolean =>
    typeof v === 'boolean' ? v : fallback;
  const pickPrecedence = (v: unknown): 'minor' | 'nonCitizen' =>
    v === 'nonCitizen' ? 'nonCitizen' : 'minor';

  if (Array.isArray(ss)) {
    // Array-of-{key,value} shape
    const map = new Map<string, unknown>();
    for (const row of ss as ReadonlyArray<Record<string, unknown>>) {
      if (row && typeof row.key === 'string') map.set(row.key, row.value);
    }
    return {
      minorVsNonCitizen: pickPrecedence(map.get('precedence.minorVsNonCitizen')),
      autoPromoteOnReload: pickBool(map.get('existingCif.autoPromoteOnReload'), defaults.autoPromoteOnReload),
      supervisorOverrideRequired: pickBool(
        map.get('existingCif.supervisorOverrideRequired'),
        defaults.supervisorOverrideRequired,
      ),
    };
  }

  if (ss && typeof ss === 'object') {
    const rec = ss as Record<string, unknown>;
    return {
      minorVsNonCitizen: pickPrecedence(rec['precedence.minorVsNonCitizen']),
      autoPromoteOnReload: pickBool(rec['existingCif.autoPromoteOnReload'], defaults.autoPromoteOnReload),
      supervisorOverrideRequired: pickBool(
        rec['existingCif.supervisorOverrideRequired'],
        defaults.supervisorOverrideRequired,
      ),
    };
  }

  return defaults;
}

// -----------------------------------------------------------------------------
// Officer supervisor-role probe
// -----------------------------------------------------------------------------

function isSupervisor(auth: ReturnType<typeof useAuth>, config: unknown): boolean {
  // BACKLOG-1787 (Bill 2026-10-16): "supervisor" is decided by the same admin
  // setting the supervisor override uses — `override.supervisor.roles`
  // (default ["Supervisor"]; an admin can add Manager). Exact role-code match,
  // replacing the old "role name contains 'supervisor'" substring probe.
  const user: unknown = (auth as unknown as { user?: unknown })?.user;
  if (!user || typeof user !== 'object') return false;
  const u = user as Record<string, unknown>;
  const allowed = supervisorOverrideRolesFrom(
    config && typeof config === 'object' ? (config as Record<string, unknown>).systemSettings : undefined,
  );
  const roles: unknown[] = Array.isArray(u.roles) ? (u.roles as unknown[]) : [];
  if (typeof u.role === 'string') roles.push(u.role);
  return holdsSupervisorOverrideRole(roles, allowed);
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * Compute the derived customerType + source for the given Person.
 *
 * Memoized on the Person inputs that actually influence the result (DOB +
 * citizenship) so re-renders caused by unrelated field edits don't thrash.
 */
export function useIndividualDerivation(
  person: RaoPerson,
  opts: UseIndividualDerivationOptions,
): DerivationResult {
  const { config } = useConfig();
  const auth = useAuth();

  return useMemo<DerivationResult>(() => {
    const toggles = readToggles(config);
    const officerIsSupervisor = isSupervisor(auth, config);

    // Rule 1: baseline
    let customerType: DerivedCustomerType = 'Individual';
    let derivedFrom: DerivationSource = 'baseline';

    // Rule 2: Age-driven Minor promotion
    const age = ageFromDob(person.dob);
    const isMinorByAge = age !== null && age < 18;
    if (isMinorByAge) {
      customerType = 'Minor';
      derivedFrom = 'dob';
    }

    // Rule 3: Citizenship-driven NonCitizen promotion
    const citizenship = person.citizenshipStatus ?? '';
    const isNonUs = citizenship !== '' && citizenship !== 'US Citizen';
    if (isNonUs) {
      if (customerType === 'Minor' && toggles.minorVsNonCitizen === 'minor') {
        // Minor wins by default — keep Minor, leave from='dob'
      } else {
        customerType = 'NonCitizen';
        derivedFrom = 'citizenship';
      }
    }

    // Existing-CIF reload — auto-promote / stored-type handling
    if (opts.mode === 'existing' && opts.storedType && opts.storedType !== customerType) {
      // Bill 2026-04-26: when the stored value is just a legacy Core Director
      // single-letter code that maps unambiguously to the derived v3 label,
      // don't trigger supervisor approval — it's a label normalization, not
      // a real promotion. Only ambiguous codes (or genuine mismatches) need
      // a supervisor.
      const LEGACY_CODE_TO_LABEL: Record<string, DerivedCustomerType> = {
        I: 'Individual',
        M: 'Minor',
        N: 'NonCitizen',
      };
      const normalizedFromCode = LEGACY_CODE_TO_LABEL[opts.storedType.trim()];
      if (normalizedFromCode && normalizedFromCode === customerType) {
        // Legacy code matches the derived label — silently normalize, no banner.
        return { customerType, derivedFrom };
      }

      if (!toggles.autoPromoteOnReload) {
        // Don't promote — return the stored value (and zero signal otherwise).
        return {
          customerType: (opts.storedType as DerivedCustomerType) ?? customerType,
          derivedFrom: 'baseline',
        };
      }
      // Bill ruling 2026-07-27 (BACKLOG-1431): the Individual → Minor
      // promotion is derived from the DOB calculation, so it never needs a
      // supervisor. Only genuine mismatches (citizenship flips, stored codes
      // that don't map) keep the gate.
      const storedTrimmed = opts.storedType.trim();
      const isDobDerivedMinorPromotion =
        customerType === 'Minor' &&
        derivedFrom === 'dob' &&
        (storedTrimmed === 'I' || storedTrimmed === 'Individual');
      const requiresSupervisorApproval =
        toggles.supervisorOverrideRequired &&
        !officerIsSupervisor &&
        !isDobDerivedMinorPromotion;
      return {
        customerType,
        derivedFrom,
        promotedFrom: opts.storedType,
        ...(requiresSupervisorApproval ? { requiresSupervisorApproval: true } : {}),
      };
    }

    return { customerType, derivedFrom };
  }, [person.dob, person.citizenshipStatus, opts.mode, opts.storedType, config, auth]);
}
